<template>
  <PopupContent
    v-show="showPopup"
    class="bubble-popup"
    :style="popupStyle"
    :disable-chart="true"
    :is-mobile="true"
    :popup-details="activePopupDetails"
    @updated="updatePopupCardSize"
  />
</template>

<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component'

import PopupContent from '@/components/public/Popup/PopupContent.vue'

const components = {
  PopupContent,
}

const POPUP_CARD_WIDTH = 300

// eslint-disable-next-line no-use-before-define
@Component<MobileBubblePopup>({ components })
export default class MobileBubblePopup extends Vue {
  popupCardHeight: number = 0

  get activePopupDetails() {
    return this.$typedStore.activeVisualisation.activeBubblesPopupDetails
  }

  get showPopup() {
    return this.activePopupDetails !== undefined
  }

  get isLandscape() {
    return this.$typedStore.public.display.isLandscape
  }

  get bubblesContainerDimensions() {
    return this.$typedStore.public.size.bubblesContainerDimensions
  }

  get popupStyle() {
    return {
      ...(this.position?.top ? { top: this.position.top } : {}),
      ...(this.position?.left ? { left: this.position.left } : {}),
      width: POPUP_CARD_WIDTH + 'px',
      transform: `scale(${this.transformCardScale})`,
    }
  }

  get maxAvailableContainerHeight() {
    // 90% of the available height so that the popup does not touch the top or bottom edges
    return this.bubblesContainerDimensions.height * 0.9
  }

  get maxAvailableContainerWidth() {
    // 90% of the available width so that the popup does not touch the left or right edges
    return this.bubblesContainerDimensions.width * 0.9
  }

  get transformCardScale() {
    if (this.isLandscape) {
      if (this.popupCardHeight > this.maxAvailableContainerHeight) {
        const scale = this.maxAvailableContainerHeight / this.popupCardHeight
        return Math.min(scale, 1)
      }
    } else {
      if (POPUP_CARD_WIDTH > this.maxAvailableContainerWidth) {
        const scale = this.maxAvailableContainerWidth / POPUP_CARD_WIDTH
        return Math.min(scale, 1)
      }
    }

    return 1
  }

  get position() {
    if (this.activePopupDetails) {
      const bubbleRadius = this.activePopupDetails.radius
      const distanceFromBubble = 20 * this.$typedStore.public.size.bubblesVisualisationScaleFactor
      const top = this.calculatePopupTop(bubbleRadius, distanceFromBubble)
      const left = this.calculatePopupLeft(bubbleRadius, distanceFromBubble)

      return { left: `${left}px`, top: `${top}px` }
    }

    return {}
  }

  calculatePopupTop(bubbleRadius: number, distanceFromBubble: number) {
    const bubbleCenterY = this.activePopupDetails!.position.y
    if (this.isLandscape) {
      const containerHeight = this.bubblesContainerDimensions.height
      // the ideal top position is to align the center of the popup with the center of the bubble
      let popupTop = bubbleCenterY - this.popupCardHeight / 2

      const popupBottom = popupTop + this.popupCardHeight
      const minEdgeGap = (containerHeight - this.maxAvailableContainerHeight) / 2

      // ensure the popup does not display outside the top or bottom of the container
      if (popupTop < minEdgeGap) {
        popupTop = minEdgeGap
      } else if (popupBottom > containerHeight - minEdgeGap) {
        popupTop = containerHeight - minEdgeGap - this.popupCardHeight
      }
      return popupTop
    } else {
      return (
        bubbleCenterY -
        bubbleRadius -
        this.popupCardHeight -
        distanceFromBubble +
        this.bubblesContainerTop()
      )
    }
  }

  calculatePopupLeft(bubbleRadius: number, distanceFromBubble: number) {
    if (this.isLandscape) {
      return (
        this.activePopupDetails!.position.x -
        bubbleRadius -
        POPUP_CARD_WIDTH -
        distanceFromBubble +
        this.bubblesContainerLeft()
      )
    } else {
      return (this.bubblesContainerDimensions.width - POPUP_CARD_WIDTH) / 2
    }
  }

  bubblesContainerBoundingClientRect() {
    return document.querySelector('#bubblesContainer')?.getBoundingClientRect()
  }

  bubblesContainerLeft() {
    return this.bubblesContainerBoundingClientRect()?.left ?? 0
  }

  bubblesContainerTop() {
    return this.bubblesContainerBoundingClientRect()?.top ?? 0
  }

  updatePopupCardSize(newValue: { clientHeight: number }) {
    this.popupCardHeight = newValue.clientHeight
  }
}
</script>

<style scoped>
.bubble-popup {
  position: absolute;
  z-index: 4;
  width: 300px;
  opacity: 0.97;
}
</style>
